import { useMemo, useState, useEffect } from 'react';

import { useMapData } from './use-map-data';
import { useFilterMapData } from './use-filter-poi-map-data';
import { fetchDataFromPublicDir } from './fetch-data-from-public-dir';

export interface CaseStudy {
  id: string;
  title: string;
  url: string;
  description: string;
  date: string | null;
  programme_id: string;
  is_external: boolean;
}

export interface CaseStudyWithLocation extends CaseStudy {
  latitude: number;
  longitude: number;
  studyName: string;
  studyAcronym: string;
  country: string;
  city: string;
  region: string;
  /** The specific programme ID this entry is linked to (may differ from programme_id if case study has multiple) */
  linkedProgrammeId: string;
}

/**
 * Hook to load case studies data and join with study location data
 */
export const useCaseStudiesData = () => {
  const { loading: mapDataLoading } = useMapData();
  const { filteredStudiesById } = useFilterMapData();
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  // Load case studies data once on mount
  useEffect(() => {
    const loadCaseStudies = async () => {
      try {
        const data = await fetchDataFromPublicDir('poi', 'case-studies.json');
        setCaseStudies(data);
        setLoading(false);
      } catch (exception) {
        console.error('Error loading case studies:', exception);
        setError(
          exception instanceof Error ? exception : new Error(String(exception)),
        );
        setLoading(false);
      }
    };

    loadCaseStudies();
  }, []);

  // Joined case studies with location data, filtered by current POI filters
  // Case studies with multiple programme_ids (comma-separated) create an entry for each valid study
  const caseStudiesWithLocation = useMemo(() => {
    if (!caseStudies || !filteredStudiesById || mapDataLoading || loading) {
      return [];
    }

    return caseStudies.flatMap((caseStudy): CaseStudyWithLocation[] => {
      // Split programme_id by comma to handle multiple linked programmes
      const programmeIds = caseStudy.programme_id
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      // Create an entry for each valid programme
      return programmeIds
        .map((programmeId): CaseStudyWithLocation | null => {
          const matchingStudy = filteredStudiesById[programmeId];

          if (!matchingStudy) {
            // Study has been filtered out or doesn't exist
            return null;
          }

          // Parse coordinates from the study data
          const latitude =
            typeof matchingStudy.latitude === 'string'
              ? Number.parseFloat(matchingStudy.latitude)
              : matchingStudy.latitude;
          const longitude =
            typeof matchingStudy.longitude === 'string'
              ? Number.parseFloat(matchingStudy.longitude)
              : matchingStudy.longitude;

          if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
            console.warn(
              `Invalid coordinates for case study: ${caseStudy.title} (programme: ${programmeId})`,
            );
            return null;
          }

          return {
            ...caseStudy,
            latitude,
            longitude,
            studyName: matchingStudy.studyName,
            studyAcronym: matchingStudy.studyAcronym || '',
            country: matchingStudy.country,
            city: matchingStudy.city,
            region: matchingStudy.region,
            linkedProgrammeId: programmeId,
          };
        })
        .filter((item): item is CaseStudyWithLocation => item !== null);
    });
  }, [caseStudies, filteredStudiesById, mapDataLoading, loading]);

  // Offset case studies that share the same coordinates so they don't overlap
  const offsetCaseStudies = useMemo(() => {
    if (caseStudiesWithLocation.length === 0) return [];

    // Group by coordinates
    const byCoords = new Map<string, CaseStudyWithLocation[]>();
    for (const cs of caseStudiesWithLocation) {
      const key = `${cs.latitude},${cs.longitude}`;
      const existing = byCoords.get(key) || [];
      existing.push(cs);
      byCoords.set(key, existing);
    }

    // Offset items that share coordinates
    const result: CaseStudyWithLocation[] = [];
    const offsetDistance = 0.008; // degrees - roughly 800m at equator

    for (const group of byCoords.values()) {
      if (group.length === 1) {
        result.push(group[0]);
      } else {
        // Arrange in a circle around the original point
        const angleStep = (2 * Math.PI) / group.length;
        for (const [index, cs] of group.entries()) {
          const angle = angleStep * index;
          result.push({
            ...cs,
            latitude: cs.latitude + offsetDistance * Math.sin(angle),
            longitude: cs.longitude + offsetDistance * Math.cos(angle),
          });
        }
      }
    }

    return result;
  }, [caseStudiesWithLocation]);

  return {
    caseStudies: offsetCaseStudies,
    loading: loading || mapDataLoading,
    error,
  };
};
